import { QObjDBObject } from "../CoreApp/QXDoc";

type HolderCheck = (holder: QObjDBObject) => boolean;
type HoldersInput = HolderCheck | Record<string, boolean>;

/**
 * Encapsulates a position filter fit, among with everyone of its holders.
 * You can see it as a structured boolean, as you can use or/and methods to combine multiple conditions.
 */
export class PositionFilterCondition {
  public positionCondition: boolean;
  public holdersCondition: any;
  public onlyHoldersCondition: boolean = true;
  private position: QObjDBObject;

  constructor(
    position: QObjDBObject,
    positionCondition: boolean,
    holdersInput: HoldersInput,
    onlyHoldersCondition: boolean = true
  ) {
    this.position = position;
    this.positionCondition = positionCondition;
    this.holdersCondition =
      typeof holdersInput === "function"
        ? GenerateHoldersFilterResult(position, holdersInput)
        : holdersInput;
    this.onlyHoldersCondition = onlyHoldersCondition;
  }

  private static FromMerged(
    position: QObjDBObject,
    positionCondition: boolean,
    holdersCondition: Record<string, boolean>,
    onlyHoldersCondition: boolean
  ): PositionFilterCondition {
    return new PositionFilterCondition(
      position,
      positionCondition,
      holdersCondition,
      onlyHoldersCondition
    );
  }

  static AllTrue(position: QObjDBObject): PositionFilterCondition {
    return new PositionFilterCondition(position, true, () => true);
  }

  static AllFalse(position: QObjDBObject): PositionFilterCondition {
    return new PositionFilterCondition(position, false, () => false);
  }

  static FromHoldersCondition(position: QObjDBObject, check: HolderCheck): PositionFilterCondition {
    const newCondition = new PositionFilterCondition(position, true, check);

    const holdersFilter = Object.values(newCondition.holdersCondition);
    if (holdersFilter.length > 0) {
      newCondition.positionCondition = holdersFilter.some(v => v);
    } else {
      newCondition.positionCondition = false;
    }

    return newCondition;
  }

  static FromPositionCondition(
    position: QObjDBObject,
    condition: boolean
  ): PositionFilterCondition {
    return new PositionFilterCondition(position, condition, () => true, false);
  }

  and(other: PositionFilterCondition): PositionFilterCondition {
    if (this.position !== other.position) {
      throw new Error("Cannot combine conditions for different positions");
    }

    const merged: Record<string, boolean> = {};
    const keys = Object.keys(this.holdersCondition);
    for (let i = 0; i < keys.length; i++) {
      const id = keys[i];
      merged[id] = this.holdersCondition[id] && other.holdersCondition[id];
    }

    return PositionFilterCondition.FromMerged(
      this.position,
      this.positionCondition && other.positionCondition,
      merged,
      this.onlyHoldersCondition && other.onlyHoldersCondition
    );
  }

  or(other: PositionFilterCondition): PositionFilterCondition {
    if (this.position !== other.position) {
      throw new Error("Cannot combine conditions for different positions");
    }

    const merged: Record<string, boolean> = {};
    const keys = Object.keys(this.holdersCondition);
    for (let i = 0; i < keys.length; i++) {
      const id = keys[i];
      merged[id] = this.holdersCondition[id] || other.holdersCondition[id];
    }

    return PositionFilterCondition.FromMerged(
      this.position,
      this.positionCondition || other.positionCondition,
      merged,
      this.onlyHoldersCondition && other.onlyHoldersCondition
    );
  }

  getConditions() {
    return [this.onlyHoldersCondition || this.positionCondition, this.holdersCondition];
  }
}

export const GenerateHoldersFilterResult = (theObj: QObjDBObject, fn: HolderCheck) => {
  const positionPairMatchObj: any = {};
  theObj.GetHolderList()?.forEach(holder => {
    const index: any = holder.GetObjectID();
    positionPairMatchObj[index] = fn(holder);
  });
  return positionPairMatchObj;
};
